import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeftIcon, SearchIcon, HelpCircleIcon, FileTextIcon, UserIcon, CreditCardIcon, BuildingIcon, ChevronDownIcon, ChevronRightIcon, PhoneIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
const HelpPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>('general');
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };
  const toggleQuestion = (questionId: string) => {
    setExpandedQuestion(expandedQuestion === questionId ? null : questionId);
  };
  // Sample FAQ data
  const faqCategories = [{
    id: 'general',
    name: 'General Questions',
    icon: <HelpCircleIcon className="h-5 w-5" data-id="element-5309" />,
    questions: [{
      id: 'general-1',
      question: 'What is the Edo State E-Services Portal?',
      answer: 'The Edo State E-Services Portal is a digital platform that provides citizens and businesses with access to various government services online. It allows users to apply for permits, make payments, submit applications, and track the status of their requests without visiting government offices in person.'
    }, {
      id: 'general-2',
      question: 'How do I create an account?',
      answer: 'To create an account, click on the "Register" button on the homepage. You will need to provide your personal details, contact information, and create a password. Once you submit the registration form, you will receive a verification email to activate your account.'
    }, {
      id: 'general-3',
      question: 'Is my information secure on this platform?',
      answer: 'Yes, the Edo State E-Services Portal uses industry-standard security measures including 256-bit SSL encryption to protect your personal information. All data is stored securely and is only accessible to authorized personnel. We comply with relevant data protection regulations to ensure your information remains confidential.'
    }]
  }, {
    id: 'applications',
    name: 'Applications & Services',
    icon: <FileTextIcon className="h-5 w-5" data-id="element-5310" />,
    questions: [{
      id: 'app-1',
      question: 'How do I apply for a service?',
      answer: 'To apply for a service, log in to your account, navigate to the "Services" section, and select the appropriate Ministry, Department, or Agency (MDA). Browse the available services, select the one you need, and follow the on-screen instructions to complete and submit your application.'
    }, {
      id: 'app-2',
      question: 'How can I track my application status?',
      answer: 'You can track your application status by logging into your account and visiting the "My Applications" section. Here, you will see a list of all your applications with their current status. You can click on any application to view more details and track its progress.'
    }, {
      id: 'app-3',
      question: 'What documents do I need to upload for my application?',
      answer: 'The required documents vary depending on the service you are applying for. When you select a service, the system will provide a list of all required documents. Common requirements include proof of identity, proof of address, passport photographs, and relevant certificates or permits.'
    }]
  }, {
    id: 'payments',
    name: 'Payments & Fees',
    icon: <CreditCardIcon className="h-5 w-5" data-id="element-5311" />,
    questions: [{
      id: 'pay-1',
      question: 'What payment methods are accepted?',
      answer: 'The portal accepts various payment methods including credit/debit cards, bank transfers, USSD payments, and mobile money. All payments are processed securely through our integrated payment gateway.'
    }, {
      id: 'pay-2',
      question: 'How do I get a receipt for my payment?',
      answer: 'After a successful payment, a digital receipt is automatically generated and can be downloaded from the "Payment History" section of your account. You will also receive a copy of the receipt via email.'
    }, {
      id: 'pay-3',
      question: 'What should I do if my payment fails?',
      answer: 'If your payment fails, first check that your payment details were entered correctly. If the issue persists, wait a few minutes and try again. If your account was debited but the payment is not reflected on the portal, please contact our support team with your transaction reference for assistance.'
    }]
  }, {
    id: 'account',
    name: 'Account Management',
    icon: <UserIcon className="h-5 w-5" data-id="element-5312" />,
    questions: [{
      id: 'acc-1',
      question: 'How do I reset my password?',
      answer: 'To reset your password, click on the "Forgot Password" link on the login page. Enter your registered email address, and you will receive a password reset link. Follow the link to create a new password for your account.'
    }, {
      id: 'acc-2',
      question: 'How do I update my profile information?',
      answer: 'To update your profile information, log in to your account and navigate to the "My Profile" section. Click on the "Edit Profile" button, make the necessary changes, and save your updates.'
    }, {
      id: 'acc-3',
      question: 'Can I have multiple accounts?',
      answer: 'No, each individual should have only one account. Creating multiple accounts is against our terms of service and may result in account suspension. If you need to manage services for both personal and business purposes, you can do so from a single account.'
    }]
  }, {
    id: 'technical',
    name: 'Technical Support',
    icon: <BuildingIcon className="h-5 w-5" data-id="element-5313" />,
    questions: [{
      id: 'tech-1',
      question: 'What browsers are supported?',
      answer: 'The Edo State E-Services Portal is compatible with modern web browsers including Google Chrome, Mozilla Firefox, Safari, and Microsoft Edge. For the best experience, we recommend using the latest version of these browsers.'
    }, {
      id: 'tech-2',
      question: 'The website is not loading properly. What should I do?',
      answer: 'If the website is not loading properly, try clearing your browser cache and cookies, then reload the page. If the issue persists, try using a different browser or device. If you still experience problems, please contact our technical support team.'
    }, {
      id: 'tech-3',
      question: 'Can I use the portal on my mobile device?',
      answer: 'Yes, the Edo State E-Services Portal is fully responsive and can be accessed on smartphones and tablets. You can use all features of the portal on your mobile device just as you would on a desktop computer.'
    }]
  }];
  // Filter FAQs based on search term
  const filteredFAQs = searchTerm ? faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q => q.question.toLowerCase().includes(searchTerm.toLowerCase()) || q.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  })).filter(category => category.questions.length > 0) : faqCategories;
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-5314">
      <Header data-id="element-5315" />
      <main className="flex-grow py-12 px-4 sm:px-6 lg:px-8" data-id="element-5316">
        <div className="max-w-7xl mx-auto" data-id="element-5317">
          <Link to="/" className="inline-flex items-center text-green-600 hover:text-green-700 mb-6" data-id="element-5318">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-5319" />
            Back to Home
          </Link>
          <div className="text-center mb-10" data-id="element-5320">
            <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-5321">
              Help Center
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-5322">
              Find answers to common questions about the Edo State E-Services
              Portal
            </p>
            {/* Search Bar */}
            <div className="mt-6 max-w-xl mx-auto" data-id="element-5323">
              <div className="relative" data-id="element-5324">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5325">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5326" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search for answers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-5327" />
              </div>
            </div>
          </div>
          {/* FAQ Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8" data-id="element-5328">
            <div className="border-b border-gray-200 px-6 py-4" data-id="element-5329">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-5330">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-5331">
              {filteredFAQs.length > 0 ? filteredFAQs.map(category => <div key={category.id} className="py-4 px-6" data-id="element-5332">
                    <button className="w-full flex items-center justify-between text-left" onClick={() => toggleCategory(category.id)} data-id="element-5333">
                      <div className="flex items-center" data-id="element-5334">
                        <span className="mr-3 text-gray-500" data-id="element-5335">
                          {category.icon}
                        </span>
                        <span className="text-lg font-medium text-gray-900" data-id="element-5336">
                          {category.name}
                        </span>
                      </div>
                      {expandedCategory === category.id ? <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-5337" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500" data-id="element-5338" />}
                    </button>
                    {expandedCategory === category.id && <div className="mt-4 space-y-4" data-id="element-5339">
                        {category.questions.map(faq => <div key={faq.id} className="border border-gray-200 rounded-md overflow-hidden" data-id="element-5340">
                            <button className="w-full flex items-center justify-between bg-gray-50 px-4 py-3 text-left" onClick={() => toggleQuestion(faq.id)} data-id="element-5341">
                              <span className="font-medium text-gray-900" data-id="element-5342">
                                {faq.question}
                              </span>
                              {expandedQuestion === faq.id ? <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-5343" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500" data-id="element-5344" />}
                            </button>
                            {expandedQuestion === faq.id && <div className="px-4 py-3 bg-white" data-id="element-5345">
                                <p className="text-gray-600" data-id="element-5346">{faq.answer}</p>
                              </div>}
                          </div>)}
                      </div>}
                  </div>) : <div className="py-8 px-6 text-center" data-id="element-5347">
                  <HelpCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" data-id="element-5348" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5349">
                    No results found
                  </h3>
                  <p className="text-gray-600" data-id="element-5350">
                    We couldn't find any FAQs matching your search. Please try
                    different keywords or contact our support team.
                  </p>
                </div>}
            </div>
          </div>
          {/* Contact Support Section */}
          <div className="bg-green-50 rounded-lg shadow-sm p-6 border border-green-100" data-id="element-5351">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-5352">
              Still Need Help?
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-5353">
              If you couldn't find the answer you were looking for, our support
              team is here to help.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5354">
              <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5355">
                <PhoneIcon className="h-6 w-6 text-green-600 mb-3" data-id="element-5356" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5357">
                  Call Us
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-5358">
                  Monday to Friday, 8am to 5pm
                </p>
                <p className="text-green-600 font-medium" data-id="element-5359">+234 800 EDO HELP</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5360">
                <MailIcon className="h-6 w-6 text-green-600 mb-3" data-id="element-5361" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5362">
                  Email Support
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-5363">
                  We'll respond within 24 hours
                </p>
                <p className="text-green-600 font-medium" data-id="element-5364">
                  support@edostate.gov.ng
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5365">
                <MessageSquareIcon className="h-6 w-6 text-green-600 mb-3" data-id="element-5366" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5367">
                  Live Chat
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-5368">
                  Chat with our support agents
                </p>
                <Link to="/support" className="text-green-600 font-medium hover:text-green-700" data-id="element-5369">
                  Start a conversation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5370" />
    </div>;
};
export default HelpPage;